/**
 * ErrorBoundary — app-root render guard.
 *
 * The Customer Portal previously had no error boundary anywhere in the tree, so
 * a render throw in any route silently blanked `#root` (white screen, HTTP 200,
 * no visible error). This class component (the only React API that can catch a
 * render throw) renders a user-facing fallback instead so the page is never
 * blank.
 *
 * Logging is deliberately minimal: we log ONLY the error name and the React
 * `digest` (a hashed identifier) — never the full message or the component
 * stack, since a thrown value could embed a token, cookie, PII, or an API
 * response body. Do not widen this to `error.message` / `error.stack`.
 */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { BRAND } from './layout/designTokens';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Safe, PII-free diagnostics only: the error class name and React's opaque
    // `digest` (hashed). NEVER log error.message / error.stack / info here — a
    // thrown value may carry a token, cookie, or API response body.
    const digest = (info as { digest?: string }).digest ?? 'n/a';
    console.error('[ErrorBoundary]', error?.name ?? 'Error', digest);
  }

  handleReload = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: '2rem 1.5rem',
          textAlign: 'center',
          background: BRAND.beige100,
          color: BRAND.navy900,
          fontFamily: 'inherit',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
          Something went wrong loading this page.
        </h1>
        <p style={{ margin: 0, maxWidth: '32rem', color: BRAND.slate700 }}>
          An unexpected error occurred. You can reload the page or return to your orders.
        </p>
        <div
          style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}
        >
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              border: `1px solid ${BRAND.navy700}`,
              borderRadius: '0.375rem',
              background: BRAND.navy700,
              color: BRAND.white,
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Reload page
          </button>
          <a
            href="/orders"
            style={{
              border: `1px solid ${BRAND.slate300}`,
              borderRadius: '0.375rem',
              background: BRAND.white,
              color: BRAND.navy900,
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Go to Orders
          </a>
        </div>
      </div>
    );
  }
}
